/**
 * GatewayFirewall — Gateway > Configure > Firewall Rules (label "Firewall" until OP#1858, one
 * letter from the Monitor list "Firewalls", now "Firewall Status").
 *
 * The sibling of Gateway > VLANs and Gateway > DHCP, and it reads as one: same header, same
 * gateway picker, same locked WAN card, same propose-then-confirm dialog. Nothing applies on
 * first click — a save or delete opens the shared review dialog, which runs POST /gateway/plan
 * (read-only) and only then offers /gateway/apply with the plan_token.
 *
 * WHAT THIS SCREEN REPLACED, AND WHY THAT MATTERS TO ITS DESIGN
 *
 * The previous "Firewall" screen was a catalog object holding an ordered allow/deny list of
 * {action, protocol, src, dst, port}. It reached no device, and it could not have: uCentral
 * has no arbitrary L3/L4 ACL. An operator could author "deny 0.0.0.0/0 → 22", see it saved,
 * and believe SSH was blocked. Nothing was — which is worse than the feature being absent.
 *
 * So the governing rule here is: SHOW ONLY WHAT THE GATEWAY WILL ACTUALLY ENFORCE, and say
 * out loud what it will not. The three controls are the three uCentral really has:
 *
 *   1. PORT FORWARDS — inbound DNAT from the internet side to one device on a LAN network.
 *   2. GUEST ISOLATION — `isolate-hosts`, which blocks every local IP range from that leg.
 *   3. BLOCKED DESTINATIONS — networks this leg may not reach through the WAN. On a gateway
 *      whose upstream carries a site-to-site VPN this is what keeps a guest network off the
 *      corporate range at the far end.
 *
 * And the ceiling is stated on the page, from the server's own `capabilities` block rather
 * than a hard-coded sentence here, so there is exactly one place that can be wrong.
 *
 * TWO DETAILS THE LAYOUT CARRIES DELIBERATELY
 *
 *   * ADDRESSES, NOT OFFSETS. The gateway stores a forward's target as a wildcard masked into
 *     the interface subnet ("0.0.0.120"), not as an address. Operators type and read real
 *     addresses; the server converts. Where the two disagree the row says so instead of
 *     quietly rendering one of them.
 *   * A NETWORK WITH NO STATIC SUBNET IS STILL LISTED. It cannot host a port forward — there
 *     is nothing to resolve an offset against — but it can be isolated, and omitting it would
 *     read as "this network has no firewall settings".
 */
import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useDevices, useGatewayFirewall } from "@/lib/api/hooks";
import type {
  GatewayFirewallNetwork,
  GatewayPlan,
  GatewayPortForward,
} from "@/types/domain";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  GatewayPicker,
  GatewayReviewDialog,
  INPUT,
  LockedWanCard,
  type PendingGatewayChange,
} from "@/components/gateway/GatewayChrome";

/** The destinations an operator almost always means by "keep the guests off my network". */
const PRIVATE_RANGES = ["10.0.0.0/8", "172.16.0.0/12", "192.168.0.0/16"];

function portLabel(forward: GatewayPortForward) {
  return forward.external_port === forward.internal_port
    ? forward.external_port
    : `${forward.external_port} → ${forward.internal_port}`;
}

export default function GatewayFirewall() {
  const { data: devices, isLoading: devicesLoading, error: devicesError } = useDevices({
    kind: "gateway",
  });
  const gateways = devices?.items ?? [];

  const [serial, setSerial] = useState<string | null>(null);
  useEffect(() => {
    const first = gateways[0];
    if (!serial && first) setSerial(first.serial);
  }, [serial, gateways]);

  const { data, isLoading, error } = useGatewayFirewall(serial ?? undefined);
  const [pending, setPending] = useState<PendingGatewayChange | null>(null);

  const pickerRows = useMemo(
    () => gateways.map((g) => ({ serial: g.serial, name: g.name })),
    [gateways],
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Firewall Rules"
          subtitle="Inbound port forwards, guest isolation, and where each network may not go"
        />
        <GatewayPicker
          serial={serial}
          gateways={pickerRows}
          loading={devicesLoading}
          onChange={setSerial}
        />
      </div>

      {devicesError && <ErrorBanner error={devicesError} />}
      {error && <ErrorBanner error={error} />}

      {!devicesLoading && gateways.length === 0 && (
        <EmptyState
          title="No gateways yet"
          hint="Adopt a gateway first. Firewall settings are per gateway, because they are part of that device's configuration."
        />
      )}

      {isLoading && <div className="text-sm text-ink-muted">Reading the gateway…</div>}

      {data && (
        <div className="mt-4 flex flex-col gap-4">
          {/* The ceiling, stated from the server's own capabilities block. This is the whole
              reason the screen was rebuilt, so it sits above the content, not in a tooltip. */}
          <div role="status" className="md-card p-4 text-sm">
            <div className="font-medium text-ink">What this gateway enforces</div>
            <p className="mt-1 text-ink-muted">{data.capabilities.note}</p>
            {!data.capabilities.outbound_rules && (
              <p className="mt-2 text-ink-subtle">
                To stop a network reaching somewhere, add a blocked destination to that
                network below — it is the control this platform actually applies.
              </p>
            )}
          </div>

          <Summary data={data} />

          {data.upstream.map((wan) => (
            <LockedWanCard
              key={wan.name}
              name={wan.name}
              summary="Internet side — inbound traffic arrives here"
              ports={wan.services}
              reason="Port forwards are configured on the LAN network holding the device they point at; the gateway pairs each one with this side for you."
            />
          ))}

          {data.networks.length === 0 && (
            <EmptyState
              title="No LAN networks on this gateway"
              hint="Add a network on the VLANs screen first — firewall settings apply per network."
            />
          )}

          {data.networks.map((network) => (
            <NetworkCard
              key={network.name}
              network={network}
              onChange={setPending}
            />
          ))}
        </div>
      )}

      {pending && serial && (
        <GatewayReviewDialog
          serial={serial}
          change={pending}
          onClose={() => setPending(null)}
          onApplied={() => setPending(null)}
          delta={(plan) => <FirewallDelta plan={plan} change={pending} />}
          outcomeText={(result) =>
            result.changed
              ? "The gateway accepted the new firewall settings."
              : "Nothing changed — the gateway already had these settings."
          }
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------- */

function Summary({ data }: { data: NonNullable<ReturnType<typeof useGatewayFirewall>["data"]> }) {
  const items = [
    { label: "Port forwards", value: data.summary.port_forwards },
    { label: "Isolated networks", value: data.summary.isolated_networks },
    { label: "Networks with blocks", value: data.summary.networks_with_blocks },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map((item) => (
        <div key={item.label} className="md-card p-3">
          <div className="text-2xl font-semibold tabular-nums text-ink">{item.value}</div>
          <div className="text-xs text-ink-muted">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

function NetworkCard({
  network,
  onChange,
}: {
  network: GatewayFirewallNetwork;
  onChange: (change: PendingGatewayChange) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<GatewayPortForward | null>(null);
  const [blocking, setBlocking] = useState(false);

  return (
    <section className="md-card p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-medium text-ink">{network.name}</h2>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-ink-muted">
            {network.vlan != null && <span>VLAN {network.vlan}</span>}
            <span>{network.subnet ?? "No fixed address range"}</span>
          </div>
        </div>
        <RoleGate min="org_admin" mode="hide">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={!network.addressable}
              title={
                network.addressable
                  ? undefined
                  : "This network has no fixed address range, so there is no way to say which device a port forward points at."
              }
              onClick={() => setAdding(true)}
            >
              Add port forward
            </Button>
          </div>
        </RoleGate>
      </header>

      {/* --- guest isolation ------------------------------------------------------------ */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-3 border-t border-line pt-3">
        <div className="min-w-0">
          <div className="text-sm text-ink">Guest isolation</div>
          <p className="text-xs text-ink-muted">
            {network.isolate_hosts
              ? "On — devices here reach the internet only, not each other or any local network."
              : "Off — devices here can reach other local networks this gateway can route to."}
          </p>
        </div>
        <RoleGate min="org_admin" mode="hide">
          <Button
            variant="secondary"
            onClick={() =>
              onChange({
                intent: "firewall.isolation",
                spec: { interface: network.name, isolate: !network.isolate_hosts },
                label: network.isolate_hosts ? "Turn isolation off" : "Turn isolation on",
              })
            }
          >
            {network.isolate_hosts ? "Turn off" : "Turn on"}
          </Button>
        </RoleGate>
      </div>

      {/* --- blocked destinations ------------------------------------------------------- */}
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3 border-t border-line pt-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-ink">Blocked destinations</div>
          {network.blocked_destinations.length === 0 ? (
            <p className="text-xs text-ink-muted">
              None — this network can reach anywhere the gateway can route to, including
              across any site-to-site VPN on the internet side.
            </p>
          ) : (
            <ul className="mt-1 flex flex-wrap gap-1">
              {network.blocked_destinations.map((subnet) => (
                <li
                  key={subnet}
                  className="rounded border border-line px-1.5 py-0.5 font-mono text-xs text-ink-muted"
                >
                  {subnet}
                </li>
              ))}
            </ul>
          )}
        </div>
        <RoleGate min="org_admin" mode="hide">
          <Button variant="secondary" onClick={() => setBlocking(true)}>
            Edit blocks
          </Button>
        </RoleGate>
      </div>

      {/* --- port forwards -------------------------------------------------------------- */}
      <div className="mt-3 border-t border-line pt-3">
        <div className="text-sm text-ink">Port forwards</div>
        {network.port_forwards.length === 0 ? (
          <p className="text-xs text-ink-muted">
            None. Traffic from the internet cannot reach a device on this network unless a
            forward sends it there.
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1">
            {network.port_forwards.map((forward) => (
              <li
                key={`${forward.protocol}-${forward.external_port}`}
                className="flex flex-wrap items-center gap-3 rounded border border-line px-2 py-1.5 text-sm"
              >
                <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs uppercase text-ink-muted">
                  {forward.protocol}
                </span>
                <span className="font-mono tabular-nums text-ink">{portLabel(forward)}</span>
                <span className="text-ink-subtle">→</span>
                <span className="font-mono text-ink">
                  {forward.internal_address ?? (
                    /* The offset cannot be resolved without a subnet. Show the stored value
                       rather than a blank, so the row explains itself. */
                    <span className="text-warn">
                      {forward.internal_address_offset} (unresolved)
                    </span>
                  )}
                </span>
                <RoleGate min="org_admin" mode="hide">
                  <span className="ml-auto flex items-center gap-1">
                    <Button variant="ghost" onClick={() => setEditing(forward)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        onChange({
                          intent: "firewall.forward.delete",
                          spec: {
                            interface: network.name,
                            forwards: [
                              {
                                protocol: forward.protocol,
                                external_port: forward.external_port,
                              },
                            ],
                          },
                          label: "Remove port forward",
                        })
                      }
                    >
                      Remove
                    </Button>
                  </span>
                </RoleGate>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(adding || editing) && (
        <ForwardDialog
          network={network}
          forward={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSubmit={(change) => {
            setAdding(false);
            setEditing(null);
            onChange(change);
          }}
        />
      )}
      {blocking && (
        <BlocksDialog
          network={network}
          onClose={() => setBlocking(false)}
          onSubmit={(change) => {
            setBlocking(false);
            onChange(change);
          }}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------------------- */

function ForwardDialog({
  network,
  forward,
  onClose,
  onSubmit,
}: {
  network: GatewayFirewallNetwork;
  forward: GatewayPortForward | null;
  onClose: () => void;
  onSubmit: (change: PendingGatewayChange) => void;
}) {
  const editing = !!forward;
  const [protocol, setProtocol] = useState<"tcp" | "udp" | "any">(forward?.protocol ?? "tcp");
  const [externalPort, setExternalPort] = useState(forward?.external_port ?? "");
  const [internalPort, setInternalPort] = useState(forward?.internal_port ?? "");
  const [address, setAddress] = useState(forward?.internal_address ?? "");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      intent: "firewall.forward.upsert",
      spec: {
        interface: network.name,
        protocol,
        external_port: externalPort,
        // Sent only when the operator set one; the server defaults it to the external port
        // and writes it explicitly, so the stored document never relies on an omission.
        internal_port: internalPort || undefined,
        internal_address: address,
        ...(editing
          ? {
              original_protocol: forward!.protocol,
              original_external_port: forward!.external_port,
            }
          : {}),
      },
      label: editing ? "Save port forward" : "Add port forward",
    });
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-[min(30rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-base font-medium text-ink">
            {editing ? "Edit port forward" : `Add a port forward on ${network.name}`}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Traffic arriving from the internet on this port is sent to one device on{" "}
            {network.subnet ?? "this network"}.
          </Dialog.Description>

          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink">Protocol</span>
              <select
                className={INPUT}
                value={protocol}
                onChange={(e) => setProtocol(e.target.value as "tcp" | "udp" | "any")}
              >
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
                <option value="any">TCP and UDP</option>
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink">Port on the internet side</span>
              <input
                className={INPUT}
                required
                value={externalPort}
                onChange={(e) => setExternalPort(e.target.value)}
                placeholder="443, or a range like 8000:8010"
              />
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink">
                Port on the device{" "}
                <span className="text-ink-subtle">(optional — same as above if blank)</span>
              </span>
              <input
                className={INPUT}
                value={internalPort}
                onChange={(e) => setInternalPort(e.target.value)}
                placeholder={String(externalPort || "443")}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink">Device address</span>
              <input
                className={INPUT}
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={network.subnet ? network.subnet.split("/")[0] : "192.168.1.50"}
              />
              <span className="text-xs text-ink-subtle">
                Must be a device on {network.subnet ?? "this network"}. Give it a fixed IP on
                the DHCP screen first, or the forward will point at whatever holds that
                address later.
              </span>
            </label>

            <div className="md-dialog-footer flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Review change
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function BlocksDialog({
  network,
  onClose,
  onSubmit,
}: {
  network: GatewayFirewallNetwork;
  onClose: () => void;
  onSubmit: (change: PendingGatewayChange) => void;
}) {
  const [text, setText] = useState(network.blocked_destinations.join("\n"));

  function submit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      intent: "firewall.blocked",
      spec: {
        interface: network.name,
        // The full intended list, not a delta: the box shows everything and submits
        // everything, so clearing it here clears it on the gateway.
        subnets: text
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      },
      label: "Save blocked destinations",
    });
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-[min(30rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-base font-medium text-ink">
            Where {network.name} may not go
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Traffic from this network toward these ranges is dropped at the gateway. One per
            line. Leave it empty to remove every block.
          </Dialog.Description>

          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <textarea
              className={`${INPUT} min-h-[7rem] font-mono text-sm`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="192.168.10.0/24"
            />
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-ink-subtle">Common:</span>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setText(PRIVATE_RANGES.join("\n"))}
              >
                All private ranges
              </Button>
            </div>
            <div className="md-dialog-footer flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Review change
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The row-level before/after the review dialog renders above the raw document diff. */
function FirewallDelta({ plan, change }: { plan: GatewayPlan; change: PendingGatewayChange }) {
  if (!plan.changed) {
    return (
      <p className="text-sm text-ink-muted">
        This gateway already has these settings — applying would change nothing.
      </p>
    );
  }
  const spec = change.spec;
  return (
    <div className="text-sm text-ink">
      {change.intent === "firewall.isolation" && (
        <p>
          Guest isolation on <strong>{spec.interface}</strong> will be turned{" "}
          <strong>{spec.isolate ? "on" : "off"}</strong>.
          {spec.isolate
            ? " Devices there will reach the internet only."
            : " Devices there will be able to reach other local networks again."}
        </p>
      )}
      {change.intent === "firewall.blocked" && (
        <p>
          <strong>{spec.interface}</strong> will be blocked from{" "}
          {spec.subnets && spec.subnets.length > 0 ? (
            <strong>{spec.subnets.join(", ")}</strong>
          ) : (
            <strong>nothing — every existing block is removed</strong>
          )}
          .
        </p>
      )}
      {change.intent === "firewall.forward.upsert" && (
        <p>
          {spec.protocol?.toUpperCase()} port <strong>{String(spec.external_port)}</strong> from
          the internet will be sent to <strong>{spec.internal_address}</strong> on{" "}
          <strong>{spec.interface}</strong>. This makes that device reachable from the
          internet.
        </p>
      )}
      {change.intent === "firewall.forward.delete" && (
        <p>
          The forward for{" "}
          <strong>
            {spec.forwards?.map((f) => `${f.protocol} ${f.external_port}`).join(", ")}
          </strong>{" "}
          will be removed from <strong>{spec.interface}</strong>. That device will no longer be
          reachable from the internet on that port.
        </p>
      )}
    </div>
  );
}
